import { connectorBindingTooltips } from '../../../shared/config/wizardTooltips';
import { formatParameterSetupTargetLabel } from '../../../shared/ui/workflow/ParameterSetupScopePanel';

export type ConnectorFieldKey =
  | 'endpoint1Value'
  | 'endpoint2Value'
  | 'endpoint3Value'
  | 'endpoint4Value'
  | 'materialValue'
  | 'designStdValue'
  | 'pressureClassValue'
  | 'insulationSpecValue'
  | 'insulationThicknessValue'
  | 'tracingSpecValue'
  | 'itemDescriptionValue';

export type SectionKey = 'connectors' | 'bom' | 'components';
export type OptionGroupKey = 'shared' | 'insulationSpec' | 'insulationThickness';

export interface ConnectorFieldDefinition {
  field: ConnectorFieldKey;
  label: string;
  required: boolean;
  help: (typeof connectorBindingTooltips)[keyof typeof connectorBindingTooltips];
  optionGroup: OptionGroupKey;
  focusTokens: string[];
}

export interface ConnectorSectionDefinition {
  key: SectionKey;
  title: string;
  shortTitle: string;
  description: string;
  fields: ConnectorFieldDefinition[];
}

export function describeConnectorTargetApplication(
  destination: 'project' | 'family',
  bindingKind: 'type' | 'instance'
): string {
  if (destination === 'family') {
    return `Revit stores connector values on pipes as project ${bindingKind === 'type' ? 'TYPE' : 'INSTANCE'} parameters and on editable loadable families as family ${bindingKind === 'type' ? 'TYPE' : 'INSTANCE'} parameters.`;
  }

  return `${formatParameterSetupTargetLabel(destination, bindingKind)} stores connector values on every selected Revit category.`;
}

export const SECTION_DEFINITIONS: readonly ConnectorSectionDefinition[] = [
  {
    key: 'connectors',
    title: 'Connectors',
    shortTitle: 'Connectors',
    description: 'From the Revit parameter list, select the parameters whose values will populate the PCF connection end types.',
    fields: [
      {
        field: 'endpoint1Value',
        label: 'END-POINT_1 *',
        required: true,
        help: connectorBindingTooltips.endpoint1,
        optionGroup: 'shared',
        focusTokens: ['ENDPOINT1', 'END-POINT-1', 'END-TYPE-1'],
      },
      {
        field: 'endpoint2Value',
        label: 'END-POINT_2 *',
        required: true,
        help: connectorBindingTooltips.endpoint2,
        optionGroup: 'shared',
        focusTokens: ['ENDPOINT2', 'END-POINT-2', 'END-TYPE-2'],
      },
      {
        field: 'endpoint3Value',
        label: 'END-POINT_3',
        required: false,
        help: connectorBindingTooltips.endpoint3,
        optionGroup: 'shared',
        focusTokens: ['ENDPOINT3', 'END-POINT-3', 'END-TYPE-3'],
      },
      {
        field: 'endpoint4Value',
        label: 'END-POINT_4',
        required: false,
        help: connectorBindingTooltips.endpoint4,
        optionGroup: 'shared',
        focusTokens: ['ENDPOINT4', 'END-POINT-4', 'END-TYPE-4'],
      },
    ],
  },
  {
    key: 'bom',
    title: 'BOM settings',
    shortTitle: 'BOM',
    description: 'From the Revit parameter list, select the parameters whose values will populate the material, design standard, and pressure class fields in the BOM output.',
    fields: [
      {
        field: 'materialValue',
        label: 'Material *',
        required: true,
        help: connectorBindingTooltips.material,
        optionGroup: 'shared',
        focusTokens: ['MATERIAL'],
      },
      {
        field: 'designStdValue',
        label: 'DesignStd',
        required: false,
        help: connectorBindingTooltips.designStd,
        optionGroup: 'shared',
        focusTokens: ['DESIGN-STANDARD', 'DESIGN-STD'],
      },
      {
        field: 'pressureClassValue',
        label: 'PressureClass',
        required: false,
        help: connectorBindingTooltips.pressureClass,
        optionGroup: 'shared',
        focusTokens: ['PRESSURE-CLASS'],
      },
    ],
  },
  {
    key: 'components',
    title: 'Components',
    shortTitle: 'Components',
    description: 'From the Revit parameter list, select the parameters whose values will populate the PCF component description, insulation, and tracing data.',
    fields: [
      {
        field: 'itemDescriptionValue',
        label: 'ITEM-DESCRIPTION *',
        required: true,
        help: connectorBindingTooltips.itemDescription,
        optionGroup: 'shared',
        focusTokens: ['ITEM-DESCRIPTION'],
      },
      {
        field: 'insulationSpecValue',
        label: 'INSULATION-SPEC',
        required: false,
        help: connectorBindingTooltips.insulationSpec,
        optionGroup: 'insulationSpec',
        focusTokens: ['INSULATION-SPEC'],
      },
      {
        field: 'insulationThicknessValue',
        label: 'INSULATIONTHICKNESS',
        required: false,
        help: connectorBindingTooltips.insulationThickness,
        optionGroup: 'insulationThickness',
        focusTokens: ['INSULATIONTHICKNESS', 'INSULATION-THICKNESS'],
      },
      {
        field: 'tracingSpecValue',
        label: 'TRACING-SPEC',
        required: false,
        help: connectorBindingTooltips.tracingSpec,
        optionGroup: 'shared',
        focusTokens: ['TRACING-SPEC'],
      },
    ],
  },
];

export const FIELD_SECTION_LOOKUP: Record<ConnectorFieldKey, SectionKey> = SECTION_DEFINITIONS.reduce(
  (lookup, section) => {
    section.fields.forEach((field) => {
      lookup[field.field] = section.key;
    });
    return lookup;
  },
  {} as Record<ConnectorFieldKey, SectionKey>
);

export const DEFAULT_SECTION: SectionKey = 'connectors';

export const getSectionFromFocusState = (focusState: {
  connectorBindings: boolean;
  bomSettings: boolean;
  componentsSettings: boolean;
}): SectionKey => {
  if (focusState.bomSettings) return 'bom';
  if (focusState.componentsSettings) return 'components';
  if (focusState.connectorBindings) return 'connectors';
  return DEFAULT_SECTION;
};
